import { supabase } from "../supabase";
import { getActiveOrganizationId, subscribeActiveOrg } from "../org";
import { normalizeVendor } from "../csv/normalizeVendor";
import type { Vendor, VendorStore, VendorType } from "./types";

interface VendorRow {
  id: string;
  organization_id: string;
  canonical_name: string;
  normalized_name: string;
  type: VendorType;
  country_code: string | null;
  relationship_type: string | null;
  tax_form_status_json: Record<string, unknown> | null;
  logo_url: string | null;
  created_at: string;
  updated_at: string;
}

const ROW_COLUMNS =
  "id, organization_id, canonical_name, normalized_name, type, country_code, relationship_type, tax_form_status_json, logo_url, created_at, updated_at";

function rowToVendor(row: VendorRow): Vendor {
  return {
    id: row.id,
    organizationId: row.organization_id,
    canonicalName: row.canonical_name,
    normalizedName: row.normalized_name,
    type: row.type,
    countryCode: row.country_code,
    relationshipType: row.relationship_type,
    taxFormStatusJson: row.tax_form_status_json ?? {},
    logoUrl: row.logo_url,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export function createSupabaseVendorStore(): VendorStore {
  const listeners = new Set<() => void>();

  // Stable snapshot reference — same rationale as the other Supabase stores.
  let snapshot: Vendor[] = [];
  let initialized = false;
  let loadPromise: Promise<void> | null = null;

  function emit(): void {
    for (const l of listeners) l();
  }
  function commit(next: Vendor[]): void {
    snapshot = next;
    emit();
  }

  function applyUpdate(updated: Vendor): void {
    const idx = snapshot.findIndex((v) => v.id === updated.id);
    if (idx !== -1) {
      const next = snapshot.slice();
      next[idx] = updated;
      commit(next);
    }
  }

  async function loadFromServer(): Promise<void> {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError || !userData?.user) {
      commit([]);
      return;
    }
    const orgId = getActiveOrganizationId();
    if (!orgId) {
      commit([]);
      return;
    }
    const { data: rows, error } = await supabase
      .from("vendors")
      .select(ROW_COLUMNS)
      .eq("organization_id", orgId)
      .order("canonical_name", { ascending: true });
    if (error) {
      // eslint-disable-next-line no-console
      console.warn(`[vendors] list failed: ${error.message}`);
      commit([]);
      return;
    }
    const typedRows = (rows ?? []) as VendorRow[];
    commit(typedRows.map(rowToVendor));
  }

  function ensureLoaded(): Promise<void> {
    if (initialized) return Promise.resolve();
    if (!loadPromise) {
      loadPromise = loadFromServer().finally(() => {
        initialized = true;
        loadPromise = null;
      });
    }
    return loadPromise;
  }

  let authSubAttached = false;
  function attachAuthListener(): void {
    if (authSubAttached) return;
    authSubAttached = true;
    supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") {
        initialized = false;
        commit([]);
        return;
      }
      if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
        initialized = false;
        void ensureLoaded();
      }
    });
    subscribeActiveOrg(() => {
      initialized = false;
      void ensureLoaded();
    });
  }

  return {
    list(): Vendor[] {
      if (!initialized && !loadPromise) {
        void ensureLoaded();
      }
      return snapshot;
    },

    get(id: string): Vendor | undefined {
      return snapshot.find((v) => v.id === id);
    },

    async upsertByName(rawName: string): Promise<Vendor> {
      const canonical = rawName.trim();
      if (!canonical) {
        throw new Error("Vendor name is required.");
      }
      const normalized = normalizeVendor(canonical);
      if (!normalized) {
        throw new Error("Vendor name normalizes to empty.");
      }
      const orgId = getActiveOrganizationId();
      if (!orgId) {
        throw new Error("No active organization selected.");
      }

      // Check the local snapshot first — hot path for repeat inserts within
      // a session. Saves a round trip.
      const local = snapshot.find((v) => v.normalizedName === normalized);
      if (local) return local;

      // Check the DB next in case another tab created the row.
      const { data: existing, error: selectErr } = await supabase
        .from("vendors")
        .select(ROW_COLUMNS)
        .eq("organization_id", orgId)
        .eq("normalized_name", normalized)
        .maybeSingle();
      if (selectErr) {
        throw new Error(`Vendor lookup failed: ${selectErr.message}`);
      }
      if (existing) {
        const vendor = rowToVendor(existing as VendorRow);
        // Backfill snapshot so subsequent upserts short-circuit.
        if (!snapshot.some((v) => v.id === vendor.id)) {
          commit([vendor, ...snapshot]);
        }
        return vendor;
      }

      // Create. If a parallel caller wins the race, the unique index on
      // (organization_id, normalized_name) will reject — fall through to a
      // second select.
      const { data: inserted, error: insertErr } = await supabase
        .from("vendors")
        .insert({
          organization_id: orgId,
          canonical_name: canonical,
          normalized_name: normalized,
          type: "vendor",
        })
        .select(ROW_COLUMNS)
        .single();
      if (insertErr) {
        // 23505 = unique_violation. Means another caller inserted the same
        // (org, normalized) combo between our select and insert; just read
        // it back.
        if (insertErr.code === "23505") {
          const { data: raced, error: racedErr } = await supabase
            .from("vendors")
            .select(ROW_COLUMNS)
            .eq("organization_id", orgId)
            .eq("normalized_name", normalized)
            .single();
          if (racedErr || !raced) {
            throw new Error(
              `Vendor upsert lost race and could not re-read: ${racedErr?.message ?? "unknown"}`,
            );
          }
          const vendor = rowToVendor(raced as VendorRow);
          if (!snapshot.some((v) => v.id === vendor.id)) {
            commit([vendor, ...snapshot]);
          }
          return vendor;
        }
        throw new Error(`Vendor create failed: ${insertErr.message}`);
      }
      const vendor = rowToVendor(inserted as VendorRow);
      initialized = true;
      commit([vendor, ...snapshot]);
      return vendor;
    },

    async update(
      id: string,
      patch: Partial<
        Omit<Vendor, "id" | "organizationId" | "createdAt" | "updatedAt">
      >,
    ): Promise<Vendor> {
      const dbPatch: Record<string, unknown> = {};
      if (patch.canonicalName !== undefined) {
        const canonical = patch.canonicalName.trim();
        if (!canonical) {
          throw new Error("Vendor name is required.");
        }
        const normalized = normalizeVendor(canonical);
        if (!normalized) {
          throw new Error("Vendor name normalizes to empty.");
        }
        dbPatch.canonical_name = canonical;
        dbPatch.normalized_name = normalized;
      }
      if (patch.type !== undefined) dbPatch.type = patch.type;
      if (patch.countryCode !== undefined) dbPatch.country_code = patch.countryCode;
      if (patch.relationshipType !== undefined)
        dbPatch.relationship_type = patch.relationshipType;
      if (patch.taxFormStatusJson !== undefined)
        dbPatch.tax_form_status_json = patch.taxFormStatusJson;
      if (patch.logoUrl !== undefined) dbPatch.logo_url = patch.logoUrl;

      const { data, error } = await supabase
        .from("vendors")
        .update(dbPatch)
        .eq("id", id)
        .select(ROW_COLUMNS)
        .single();
      if (error || !data) {
        throw new Error(
          `Failed to update vendor: ${error?.message ?? "unknown"}`,
        );
      }
      const updated = rowToVendor(data as VendorRow);
      applyUpdate(updated);
      return updated;
    },

    async remove(id: string): Promise<void> {
      const target = snapshot.find((v) => v.id === id);
      if (!target) return;
      // Optimistic local removal; the ON DELETE SET NULL cascade on
      // expenses.vendor_id / invoices.vendor_id happens server-side.
      const next = snapshot.filter((v) => v.id !== id);
      commit(next);
      const { error } = await supabase.from("vendors").delete().eq("id", id);
      if (error) {
        // Roll back — put the vendor back at the original position (best
        // effort; order is by canonical_name on next reload anyway).
        commit([target, ...next]);
        throw new Error(`Failed to delete vendor: ${error.message}`);
      }
    },

    async merge(sourceId: string, targetId: string): Promise<void> {
      if (sourceId === targetId) return;
      const source = snapshot.find((v) => v.id === sourceId);
      const target = snapshot.find((v) => v.id === targetId);
      if (!source || !target) {
        throw new Error("Both source and target vendors must exist.");
      }
      if (source.organizationId !== target.organizationId) {
        throw new Error("Cannot merge vendors across organizations.");
      }

      // Rewrite FKs on expenses + invoices, then delete the source. RLS on
      // update/delete enforces org scope — if the caller isn't a member,
      // one of these will throw rather than silently no-op.
      const expenseUpdate = await supabase
        .from("expenses")
        .update({ vendor_id: targetId })
        .eq("vendor_id", sourceId);
      if (expenseUpdate.error) {
        throw new Error(
          `Failed to relink expenses: ${expenseUpdate.error.message}`,
        );
      }
      const invoiceUpdate = await supabase
        .from("invoices")
        .update({ vendor_id: targetId })
        .eq("vendor_id", sourceId);
      if (invoiceUpdate.error) {
        throw new Error(
          `Failed to relink invoices: ${invoiceUpdate.error.message}`,
        );
      }

      const del = await supabase.from("vendors").delete().eq("id", sourceId);
      if (del.error) {
        throw new Error(`Failed to remove source vendor: ${del.error.message}`);
      }

      commit(snapshot.filter((v) => v.id !== sourceId));
    },

    subscribe(listener: () => void): () => void {
      attachAuthListener();
      if (!initialized && !loadPromise) {
        void ensureLoaded();
      }
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
